import { WizConstants } from '../../model/constants';
import { DATA_KIND } from '../../model/enums';
import { FormatVO } from '../../vo/workingdata/format.vo';
import { FreedataValueVO } from '../../vo/core/freedatavalue.vo';
import { Model } from '../../model/model';
import { WIZ_BOOLEAN, WizDateUtils, WizNumberUtils } from '@wizeoo/akm-core-lib';
import { Format } from '@wizeoo/akm-message-lib';

/**
 * Manager des formats pour le moteur
 */
export class FormatEngine {

    // public static NEO_FIRST_SIGN_PATTERN: RegExp = /^[-+]{1}/; // affichage du signe + ou -
    public static NUMBER_PATTERN: RegExp = /[0][.,]{0,1}[0]*/;
    public static THOUSAND_PATTERN: RegExp = /#{1}[.,_ ]{1}#{2}/; // détection du caractère de séparateur de milliers
    public static SYMBOL_PATTERN: RegExp = /\[[^0-9.,]{1,}\]/; // détection des symboles
    public static NOZERO_PATTERN: RegExp = /\[#0]/; // détection de l'expression "pas de 0"
    public static ALL_PATTERN: RegExp = new RegExp(
        FormatEngine.NOZERO_PATTERN.source +
        '|' +
        FormatEngine.SYMBOL_PATTERN.source +
        '|' +
        FormatEngine.THOUSAND_PATTERN.source +
        '|' +
        FormatEngine.NUMBER_PATTERN.source,
        'g'
    );


    protected _model: Model = null;
    constructor(model: Model) {
      this._model = model;
    }

    /* ################################################
        le constructeur (pour obtenir le bon type)
    ################################################ */

    public newFormatVO(source?: Format): FormatVO {
        return new FormatVO(source);
    }

    public getFormatList(): FormatVO[] {
        return this._model.formatList;
    }

    public getFormatMap(): Map<string, Map<string, FormatVO>> {
        return this._model.formatMap;
    }
    
    public setFormatMap(map: Map<string, Map<string, FormatVO>>) {
        this._model.formatMap = map;
    }

    /**
     * Récupération d'un format par son couple bu / code
     * @param buCode la bu de rattachement de ce style
     * @param code Le code du format
     */
    public getFormat(buCode: string, code: string): FormatVO {
        let svo: FormatVO = null;
        if (this.getFormatMap() && this.getFormatMap().get(buCode)) {
            svo = this.getFormatMap().get(buCode).get(code);
        }
        if(!!svo && !svo.partsBuilded) {
            svo.buildParts();
        }
        return svo;
    }

    /**
     * suppression de l'instance d'un formatVO
     * @param buCode la bu de rattachement de ce format
     * @param code Le code du format
     */
    public removeFormat(buCode: string, code: string): void {
        const toDie: FormatVO = this.getFormat(buCode, code);
        this._model.formatList.splice(this._model.formatList.indexOf(toDie), 1);
        this.getFormatMap().get(buCode).delete(code);
    }

    /**
     * ajout de l'instance d'un formatVO
     * @param vo formatVO
     */
    public addFormat(vo: FormatVO): void {
        if (!this._model.formatList) {
            this._model.formatList = [];
        }
        this._model.formatList.push(vo);
        if (!this.getFormatMap()) {
            this.setFormatMap(new Map<string, Map<string, FormatVO>>());
        }
        if (!this.getFormatMap().has(vo.businessUnitCode)) {
            this.getFormatMap().set(vo.businessUnitCode, new Map<string, FormatVO>());
        }
        this.getFormatMap().get(vo.businessUnitCode).set(vo.code, vo);
    }

    /**
     * Alimentation des formats du modèle
     * @param formats: Format[]
     */
    public feedFormats(formats: Format[]): void {
        const flatList: FormatVO[] = formats.map((f) => this.newFormatVO(f));

        if (this._model.formatList == null || this._model.formatList.length === 0) {
            flatList.forEach((fvo: FormatVO) => {
                this.addFormat(fvo);
            });
        } else {

            flatList.forEach((fvo: FormatVO) => {
                const fvoIndex = this._model.formatList.findIndex(element => element.businessUnitCode === fvo.businessUnitCode && element.code === fvo.code);

                if (fvoIndex < 0) {
                    this.addFormat(fvo);
                } else {
                    this._model.formatList[fvoIndex] = fvo;
                    this.getFormatMap().get(fvo.businessUnitCode).set(fvo.code, fvo);
                }
            });
        }
    }

    /**
     * moteur d'application des formats
     * @param value au format string
     * @param dataKind type de donnée
     * @param format BU|formatCode
     * @returns string formatté
     */
    public format(value: string, dataKind: string, format: string): string {
        let result: string = value;
        let formatVO: FormatVO = null;
        if (dataKind) {
            let referenceKind: string = dataKind; // la référence, par défaut, c'est le datakind.. Mais éventuellement écrasé par le format (pour les listes et objets@field)
  
            if (format) {
                formatVO = this.getFormat(
                    format.split(WizConstants.KEY_SEPARATOR)[0],
                    format.split(WizConstants.KEY_SEPARATOR)[1]
                );
                if (!!formatVO) {
                    referenceKind = formatVO.dataKind; // on prend le format comme référence, plus précis.)
                    if (!formatVO.partsBuilded) {
                        formatVO.buildParts();
                    }
                }
            }

            if (!formatVO) {
                formatVO = this.getDefaultFormat(dataKind);
            }

            switch (referenceKind) { // on prend le datakind du format. Les listes et Objets sont polymorphes
                case DATA_KIND.INT:
                case DATA_KIND.NUMBER:
                    // FormatEngine.splitNumberParts(formatRegExp);
                    if(!value || value === '0' && formatVO?.noZero) {
                        result = '';
                    } else {
                        if (!!value) { // On filtre aussi chaine vide ('') pour ne pas se retrouver avec un 0 en résultat
                            result = WizNumberUtils.formatNumber(
                                Number(value),
                                formatVO.precision,
                                formatVO.thousandSeparator,
                                formatVO.decimalSeparator
                            );
                        }
    
                        if (!result) { // interception des nulls
                            result = '';
                        }
    
                        if (formatVO.symbol) {
                            if (formatVO.symbolPosition === 'start') {
                                result = formatVO.symbol + ' ' + result;
                            } else {
                                result += ' ' + formatVO.symbol;
                            }
                        }
                    }
 
                    break;
                case DATA_KIND.DATE:
                    result = WizDateUtils.dateToString(WizDateUtils.stringToTime(value), formatVO.dateExp);
                    break;
                case DATA_KIND.TIME:
                    result = WizDateUtils.dateToString(WizDateUtils.hourToDate(value), WizDateUtils.HOUR_FORMAT_MASK);
                    break;
                case DATA_KIND.OBJECT:
                    if (!!result) {
                        const pair: string[] = value.split(WizConstants.CONTEXT_SEPARATOR);
                        if (pair.length === 2) {
                            const fdv: FreedataValueVO = this._model.freedataEngine.getFreedataValue(pair[0], pair[1]);
                            if (!!fdv) {
                                result = fdv.trsLabel;
                            }
                        }
                    }
                    break;
                case DATA_KIND.STRING:
                    result = value || '';
                    break;
                case DATA_KIND.BOOLEAN: 
                    if(formatVO?.noZero && !value) {
                        result = WIZ_BOOLEAN.FALSE;
                    } else {
                        result = value;
                    }
                    
                    break;
                case DATA_KIND.FILE:
                    try {
                        let mediaReference: any  = JSON.parse(value);
                        result = mediaReference.shortFileName || mediaReference.fileName; 
                    } catch(e: any) {
                        result = null;
                    }
                    break;
                case DATA_KIND.BINARY_MULTI:
                    if(formatVO.regExp === '1' && value) {
                        result = value.split(WizConstants.KEY_SEPARATOR)[0];
                    } else {
                        result = value;
                    }
                    break;
            }
        }
        // console.log('applyFormat', value, format, result);
        if (result === undefined || result === null) {
            result = '';
        }

        return result;
    }

    /**
     * Les formats par défaut
     */
    private  _defaultFormats: FormatVO[] = [];

    /**
     * génération des formats par défaut
     * @param dataKind le type de donnée
     */
    public getDefaultFormat(dataKind: string): FormatVO {
        let defaultFormat = this._defaultFormats[dataKind];

        if (!defaultFormat) {
            defaultFormat = this.newFormatVO();

            if (dataKind === DATA_KIND.INT) {
                defaultFormat.precision = 0;
            } else {
                defaultFormat.precision = 2;
            }
            defaultFormat.decimalSeparator = '.';
            defaultFormat.thousandSeparator = ',';
            defaultFormat.dateExp = 'dd/MM/y';

            this._defaultFormats[dataKind] = defaultFormat;
        }

        return defaultFormat;
    }

}
